// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryModel } from "@insite/client-framework/Types/ApiModels";
import Clickable from "@insite/mobius/Clickable";
import Link, { LinkProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import LazyImage from "@tps/MobiusOverrides/LazyImage/LazyImage";
import { color, xScreen } from "@tps/XDL/Utilities";
import { mergeWithCss } from "@tps/XDL/Utilities/mergeWithCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface OwnProps {
    category: Omit<CategoryModel, "subCategories">;
    categoryImage: string;
    extendedStyles?: CategoryItemStyles;
}

type Props = OwnProps;

export interface CategoryItemStyles {
    categoryItem?: InjectableCss;
    categoryTitle?: LinkProps;
    imageWrapper?: InjectableCss;
    image?: InjectableCss;
}

export const categoryItemStyles: CategoryItemStyles = {
    categoryItem: {
        css: css`
            justify-content: center;
            flex-direction: column;
            display: flex;
            width: 249px;
            ${xScreen("lt", "sm")} {
                width: 156px;
            }
        `,
    },
    categoryTitle: {
        typographyProps: {
            size: 18,
            lineHeight: "24px",
            weight: "bold",
            css: css`
                margin-bottom: 0;
                &&& {
                    color: ${color("text")};
                }
            `,
        },
        css: css`
            justify-content: center;
            text-align: center;
            margin-top: 8px;
        `,
    },
    image: {
        css: css`
            width: 100%;
            height: 100px;
            object-fit: contain;
            justify-content: center;

            ${xScreen("lte", "md")} {
                height: 130px;
            }
            ${xScreen("lt", "sm")} {
                height: 99px;
            }
            img {
                max-width: 100%;
                max-height: 100%;
                height: auto;
            }
        `,
    },
};

const CategoryItem: FC<Props> = ({ category, categoryImage, extendedStyles }) => {
    const [styles] = useState(() => mergeWithCss(categoryItemStyles, extendedStyles));

    if (!category) {
        return null;
    }

    return (
        <StyledWrapper {...styles.categoryItem}>
            <Clickable href={category.path} {...styles.imageWrapper}>
                <LazyImage
                    {...styles.image}
                    src={categoryImage}
                    altText={category.imageAltText}
                    aria-label="category image clickable"
                />
            </Clickable>
            <Link href={category.path} {...styles.categoryTitle}>
                {category.shortDescription}
            </Link>
        </StyledWrapper>
    );
};

export default CategoryItem;
